import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import _ from 'lodash';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ProductionProcessDto } from 'src/app/shared/dto/production-process.dto';
import { ProductionRouteDto } from 'src/app/shared/dto/production-route.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { ProductionProcessesService } from '../../services/production-processes.service';
import { ProductionRoutesService } from '../../services/production-routes.service';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-routes',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        CdkDropList,
        CdkDrag,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './routes.component.html',
    styleUrl: './routes.component.scss',
})
export class RoutesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    routes: ProductionRouteDto[] = [];
    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    routeSelected: ProductionRouteDto = new ProductionRouteDto();

    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;

    productionProcesses: ProductionProcessDto[] = [];
    productionProcessTableRows: ProductionProcessDto[] = [];
    previousProcessTableRows: ProductionProcessDto[] = [];
    selectedProcessess: ProductionProcessDto[] = [];

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
        description: FormControl<string>;
    }>;

    private readonly productionRoutesService = inject(ProductionRoutesService);
    private readonly productionProcessesService = inject(ProductionProcessesService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('productionRoutes', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            description: ['']
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.productionRoutesService.setupWebSocket();
        this.productionRoutesService.productionRoutes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (routes) => {
                    this.routes = routes;
                    this.routeSelected = this.routes.find(route => route.id === this.routeSelected.id) ?? new ProductionRouteDto();
                    if (this.routeSelected.id) {
                        this.productionProcessTableRows = this.routeSelected.productionProcesses!
                            .map(process => this.productionProcesses.find(p => process.productionProcessId === p.id))
                            .filter(process => process !== undefined) as ProductionProcessDto[] || [];
                    }
                    this.selectedProcessess = this.productionProcesses.filter(process => this.routeSelected.productionProcesses?.map(p => p.productionProcessId).includes(process.id));
                },
                error: (error) => {
                    this.message.create('Error al cargar las rutas de producción:', error.message);
                }
            });

        await this.productionProcessesService.setupWebSocket();
        this.productionProcessesService.productionProcessess$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (processess) => {
                    this.productionProcesses = processess;
                },
                error: (error) => {
                    this.message.create('Error al cargar los procesos de producción:', error.message);
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.productionRoutesService.disconnectWebSocket();
        this.productionProcessesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.routeSelected = new ProductionRouteDto();
        this.productionProcessTableRows = [];
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nueva ruta';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: ProductionRouteDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.routeSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo ruta ' + this.routeSelected?.name;
        this.fieldsReadOnly = true;

        this.setData();
        this.openDrawer();
    }

    edit(data: ProductionRouteDto) {
        this.routeSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando ruta ' + this.routeSelected?.name;
        this.fieldsReadOnly = false;

        this.setData();
        this.openDrawer();
    }

    setData() {
        this.productionProcessTableRows = [];

        this.productionProcessesService.sortProductionProcessByOrder(this.productionProcesses, this.routeSelected, this.selectedProcessess, this.productionProcessTableRows);

        this.previousProcessTableRows = _.cloneDeep(this.productionProcessTableRows);
    }

    async delete(routeId: number) {
        try {
            if (window.confirm('¿Estás seguro de que quieres eliminar esta ruta? Esta acción no se puede deshacer.')) {
                const deletedRoute: ProductionRouteDto = await this.productionRoutesService.deleteRoute(routeId);

                const index = this.routes.findIndex((r) => r.id === routeId);

                if (index < 0) {
                    this.message.create('error', `No se ha podido eliminar la ruta ${deletedRoute.name} con id ${routeId}, ruta no encontrada`);

                    return;
                }

                this.routes.splice(index, 1);
                const data = [...this.routes];
                this.routes = data;

                this.message.create('success', 'Ruta eliminada correctamente');
            }
        } catch (e: any) {
            this.message.create('error', `${e.message}`);
        }
    }

    closeDrawer() {
        this.selectedProcessess = [];
        this.previousProcessTableRows = [];
        this.routeSelected = new ProductionRouteDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            const routeToBeProcessed: ProductionRouteDto = {
                id: this.routeSelected.id,
                name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                productionProcessesIds: this.productionProcessTableRows.map((p) => p.id),
                description: this.validateForm.controls.description.value ? this.validateForm.controls.description.value : '',
                updatedBy: this.currentUser.id,
                createdBy: this.drawerMode === DrawerMode.CREATE ? this.currentUser.id : this.routeSelected.createdBy
            };

            if (!this.validForm(routeToBeProcessed)) {
                this.isSaving = false;
                return;
            }            

            if (this.drawerMode === DrawerMode.CREATE) {
                const createdRoute: ProductionRouteDto = await this.productionRoutesService.createRoute(routeToBeProcessed);

                this.routes.push(createdRoute);
                const data = [...this.routes];
                this.routes = data;

                this.isSaving = false;
                this.message.create('success', 'Ruta creada correctamente');
            } else if (this.drawerMode === DrawerMode.EDIT) {
                const updatedRoute: ProductionRouteDto = await this.productionRoutesService.updateRoute(routeToBeProcessed);

                const index = this.routes.findIndex((r) => r.id === updatedRoute.id);

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar la ruta ${routeToBeProcessed.name} con id ${routeToBeProcessed.id}, ruta no encontrada`);

                    return;
                }

                this.routes[index] = updatedRoute;
                const data = [...this.routes];
                this.routes = data;

                this.isSaving = false;
                this.message.create('success', 'Ruta actualizada correctamente');
            }

            this.closeDrawer();
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `Error: ${e.message}`);
        }
    }

    cancelEditing() {
        this.closeDrawer();
    }

    onProductionProcessessChange(newSelectedProductionProcessess: ProductionProcessDto[]) {
        const addedProcess: ProductionProcessDto | undefined = this.previousProcessTableRows.length === 0
            ? newSelectedProductionProcessess[0]
            : newSelectedProductionProcessess.filter(o => !this.previousProcessTableRows.some(i => i.id === o.id))[0] ?? undefined;

        if (addedProcess) {
            this.productionProcessTableRows = [
                ...this.productionProcessTableRows,
                addedProcess as ProductionProcessDto
            ];
        }

        const removedProcess = this.previousProcessTableRows.find(process =>
            !newSelectedProductionProcessess.some(newProcess => newProcess.id === process.id)
        );

        if (removedProcess) {
            this.productionProcessTableRows = this.productionProcessTableRows.filter(row => row.id !== removedProcess.id);
        }

        this.previousProcessTableRows = [...newSelectedProductionProcessess];
    }

    validForm(route: ProductionRouteDto) {
        if (!route.name) {
            this.message.create('error', 'El nombre de la ruta no puede estar vacío');
            return false;
        }
        return true;
    }

    drop(event: CdkDragDrop<string[]>): void {
        if (!this.fieldsReadOnly) {
            moveItemInArray(this.productionProcessTableRows, event.previousIndex, event.currentIndex);
        }
    }

    cloneProductionOrderProcess(idx: number): void {
        try {
            const newProductionProcess: ProductionProcessDto = _.cloneDeep(this.productionProcessTableRows[idx]) as ProductionProcessDto;

            if (!newProductionProcess) {
                this.message.create('error', 'No se ha encontrado el proceso de producción para clonar');
            }

            this.previousProcessTableRows.push(newProductionProcess);
            this.productionProcessTableRows.push(newProductionProcess);
        } catch (error: any) {
            this.message.create('error', `Error clonando el proceso de la orden de producción. Error: ${error.message}`);
        }
    }

    removeProductionProcess(idx: number): void {
        try {
            const processDeleted = this.productionProcessTableRows.splice(idx, 1);
            this.previousProcessTableRows.splice(idx, 1);

            if (!this.productionProcessTableRows.find(process => process.id === processDeleted[0].id)) {
                this.selectedProcessess = this.selectedProcessess.filter(p => p.id !== processDeleted[0].id);
            }

        } catch (error: any) {
            this.message.create('error', `Error eliminando el proceso de producción. Error: ${error.message}`);
        }
    }
}
